import { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { FilterInput } from "../shared/FilterInput";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import AppToast from "../../../../shared/components/AppToast";
import { ensureCatalogAuth } from "../../../../services/backend/authService";
import { getProductosByCriteria } from "../../../../services/backend/productoService";
import logger from "../../../../shared/utils/logger";

export const SeleccionarDestacadosDialog = ({
  visible,
  onHide,
  onConfirm,
  codigosIniciales = "",
  tipo = "B2B",
}) => {
  const toastRef = useRef(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [confirmVisible, setConfirmVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      let mounted = true;
      const load = async () => {
        setLoading(true);
        try {
          await ensureCatalogAuth();
          const response = await getProductosByCriteria({
            tipo,
            familia: 0,
            descripcion: "",
            start: 0,
            length: 9999,
          });
          const items = Array.isArray(response?.productos)
            ? response.productos
            : Array.isArray(response)
            ? response
            : [];
          if (mounted) setAllProducts(items);
        } catch (err) {
          logger.error("Error al cargar productos:", err);
          if (mounted) setAllProducts([]);
        } finally {
          if (mounted) setLoading(false);
        }
      };
      load();
      setSelectedProducts([]);

      return () => {
        mounted = false;
      };
    }
  }, [tipo, visible]);

  useEffect(() => {
    if (visible && allProducts.length && codigosIniciales) {
      const codigosSet = new Set(
        codigosIniciales
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      );
      setSelectedProducts(allProducts.filter((p) => codigosSet.has(p.codigo)));
    }
  }, [visible, allProducts, codigosIniciales]);

  const handleConfirm = () => {
    const codigos = selectedProducts.map((p) => p.codigo).join(",");
    onConfirm?.(codigos);
    onHide();
  };

  const initialCodigosSet = new Set(
    codigosIniciales
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean)
  );
  const hasChanges =
    selectedProducts.length !== initialCodigosSet.size ||
    selectedProducts.some((p) => !initialCodigosSet.has(p.codigo));

  const handleHide = () => {
    if (hasChanges) {
      setConfirmVisible(true);
    } else {
      onHide();
    }
  };

  const handleDiscard = () => {
    setConfirmVisible(false);
    onHide();
  };

  const header = (
    <div className="flex align-items-center justify-content-between flex-wrap gap-2">
      <span className="text-sm font-semibold">
        Seleccionar productos destacados ({selectedProducts.length}{" "}
        seleccionados)
      </span>
      <Button
        className="p-button-rounded p-button-sm"
        icon="pi pi-check"
        label="Confirmar"
        onClick={handleConfirm}
        disabled={!selectedProducts.length}
        severity="success"
      />
    </div>
  );

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button className="p-button-text" label="Cancelar" onClick={handleHide} />
    </div>
  );

  return (
    <>
      <AppToast ref={toastRef} />
      <ConfirmDialog
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        onConfirm={handleDiscard}
        header="Descartar cambios"
        message="Tiene productos seleccionados sin confirmar. ¿Desea descartar la selección?"
      />
      <Dialog
        header="Seleccionar Productos Destacados"
        visible={visible}
        onHide={handleHide}
        footer={footer}
        style={{ width: "80vw" }}
        maximizable
      >
        <DataTable
          value={allProducts}
          header={header}
          showGridlines
          paginator
          rows={10}
          dataKey="codigo"
          stripedRows
          filterDisplay="row"
          filter
          scrollable
          scrollHeight="50vh"
          loading={loading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          tableStyle={{ minWidth: "50rem" }}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} productos"
          selection={selectedProducts}
          onSelectionChange={(e) => setSelectedProducts(e.value)}
          selectionMode="multiple"
        >
          <Column selectionMode="multiple" headerStyle={{ width: "5%" }} />
          <Column
            field="codigo"
            header="Código"
            sortable
            style={{ width: "15%" }}
            filter
            filterElement={FilterInput}
          />
          <Column
            field="descripcion"
            header="Descripción"
            sortable
            style={{ width: "50%" }}
            filter
            filterElement={FilterInput}
          />
        </DataTable>
      </Dialog>
    </>
  );
};
